import { LoaderCircle, Square, Volume2 } from 'lucide-react';
import type { Article } from '../types';

export type ArticleSpeechLanguage = 'en' | 'hi';
export interface ArticleSpeechStatus {
  articleId: string;
  language: ArticleSpeechLanguage;
  phase: 'loading' | 'speaking';
}

interface ArticleAudioControlsProps {
  article: Article;
  status: ArticleSpeechStatus | null;
  onPlay: (article: Article, language: ArticleSpeechLanguage) => void;
}

export default function ArticleAudioControls({ article, status, onPlay }: ArticleAudioControlsProps) {
  return (
    <div className="article-audio-controls" aria-label="News audio">
      {(['en', 'hi'] as const).map((language) => {
        const active = status?.articleId === article.id && status.language === language;
        const loading = active && status.phase === 'loading';
        const speaking = active && status.phase === 'speaking';
        const code = language === 'en' ? 'EN' : 'HI';
        const label = loading ? `Translating · ${code}` : speaking ? `Stop · ${code}` : `Listen · ${code}`;
        const accessibleLabel = loading
          ? `Cancel Hindi translation for this report`
          : speaking
            ? `Stop ${language === 'en' ? 'English' : 'Hindi'} audio for this report`
            : `Listen to this report in ${language === 'en' ? 'English' : 'Hindi'}`;
        return (
          <button
            className={`article-audio-button ${language === 'hi' ? 'is-hindi' : ''} ${active ? 'is-active' : ''} ${loading ? 'is-loading' : ''}`}
            type="button"
            key={language}
            onClick={() => onPlay(article, language)}
            aria-label={accessibleLabel}
            aria-pressed={Boolean(active)}
            title={language === 'hi' ? 'Hindi speech is translated on demand and is not shown as text.' : 'Speak the English report using your browser voice.'}
          >
            {loading ? <LoaderCircle size={12} aria-hidden="true" /> : speaking ? <Square size={11} aria-hidden="true" /> : <Volume2 size={12} aria-hidden="true" />}
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
